import React, { Suspense, useEffect } from 'react'
import {
    createTheme,
    ThemeProvider,
    StyledEngineProvider,
    responsiveFontSizes,
} from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { newThemeV5 } from 'utilities/themeConfig'
import './scss/app.scss'
import { useAppSelector } from 'store/useStoreHook'
import InstallingSplashScreen from 'AppRoot/InstallingScreen/InstallingSplashScreen/InstallingSplashScreen'
import init from './utilities/yupErrorMessage/yupErrorMessage'
import useOfflineTracker from 'views/Sale/hooks/useOfflineTracker'
import ApplyGlobalStyle from 'AppRoot/ApplyGlobalStyle'
import { usePostHog } from 'posthog-js/react'
import posthogIntegration from 'utilities/posthogIntegration'
import getEnv, { isProduction } from 'utilities/getEnv'
import ErrorBoundaryPage from 'pages/ErrorBoundaryPage'
import { ErrorBoundary } from '@sentry/react'
import { Outlet, useLocation } from 'react-router-dom'
import androidWebViewApi from 'utilities/androidWebViewApi'

const DYNAMICALLY_IMPORT_ERROR = 'dynamically imported module'
function PathWatcher() {
    const location = useLocation()
    useEffect(() => {
        if (androidWebViewApi) {
            androidWebViewApi.onPathnameChanged(location.pathname)
        }
    }, [location.pathname])

    return null
}
function App() {
    init()
    useOfflineTracker()

    const posthog = usePostHog()
    const currentUser = useAppSelector((s) => s.auth.user)
    const tenantIdentifierInfo = useAppSelector(
        (s) => s.tenantIdentifierInfo.data,
    )
    const client = useAppSelector((s) => s.client.currentClient)
    const isAuthExpired = useAppSelector((s) => s.auth.isAuthExpired)
    const isLoggedOut = useAppSelector((s) => s.auth.isLoggedOut)

    useEffect(() => {
        if (isProduction() && !(getEnv('SKIP_POSTHOG') ?? false)) {
            if (currentUser && client) {
                posthogIntegration.setIdentify(
                    posthog,
                    currentUser,
                    tenantIdentifierInfo,
                    client,
                )
            }
            // Reset posthog session if user is locked out or auth expired
            else if (isAuthExpired || isLoggedOut) {
                posthogIntegration.reset(posthog)
            }
        }
    }, [
        posthog,
        currentUser,
        tenantIdentifierInfo,
        client,
        isAuthExpired,
        isLoggedOut,
    ])

    useEffect(() => {
        if (isProduction() && !(getEnv('SKIP_POSTHOG') ?? false)) {
            posthogIntegration.setGroup(posthog, tenantIdentifierInfo)
        }
    }, [posthog, tenantIdentifierInfo])

    const prefersLightMode = useAppSelector(
        (state) => state.ui.themeSetting.lightTheme,
    )

    const themes = React.useMemo(
        () =>
            responsiveFontSizes(
                createTheme(newThemeV5(prefersLightMode ? 'light' : 'dark')),
            ),
        [prefersLightMode],
    )

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes}>
                <ErrorBoundary
                    fallback={({ error }) => {
                        if (
                            (error as any).message
                                .toLocaleLowerCase()
                                .includes(DYNAMICALLY_IMPORT_ERROR)
                        ) {
                            // Use blank page
                            return <div />
                        } else return <ErrorBoundaryPage />
                    }}
                    onError={(error) => {
                        if (
                            (error as any).message
                                .toLocaleLowerCase()
                                .includes(DYNAMICALLY_IMPORT_ERROR)
                        ) {
                            // Reload immediately
                            window.location.reload()
                        } else {
                            if (
                                isProduction() ||
                                getEnv('AUTO_RELOAD_WHEN_ERROR_ON_DEV_MODE')
                            ) {
                                setTimeout(function () {
                                    window.location.reload()
                                }, 5000)
                            }
                        }
                    }}
                >
                    <CssBaseline />
                    <ApplyGlobalStyle />
                    <Suspense fallback={<InstallingSplashScreen />}>
                        <Outlet />
                    </Suspense>
                    <PathWatcher />
                </ErrorBoundary>
            </ThemeProvider>
        </StyledEngineProvider>
    )
}

export default App
